import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Project } from '@/hooks/useProjects';

// Fonction pour normaliser les accents (é -> e, à -> a, etc.)
const normalizeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
};

interface ProjectComboboxProps {
  projects: Project[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
}

export function ProjectCombobox({
  projects,
  value,
  onValueChange,
  placeholder = 'Choisir un projet',
  disabled = false,
  className,
  triggerClassName,
}: ProjectComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const activeProjects = React.useMemo(
    () => projects.filter((project) => project.status === 'active'),
    [projects]
  );

  const selectedProject = React.useMemo(
    () => activeProjects.find((project) => project.id === value),
    [activeProjects, value]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !value && 'text-muted-foreground',
            triggerClassName
          )}
          data-testid="project-combobox-trigger"
        >
          {selectedProject
            ? `${selectedProject.name}${selectedProject.client_name ? ` - ${selectedProject.client_name}` : ''}`
            : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[300px] p-0', className)} align="start">
        <Command
          filter={(value, search) => {
            const normalizedValue = normalizeAccents(value);
            const normalizedSearch = normalizeAccents(search);
            return normalizedValue.includes(normalizedSearch) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Rechercher un projet..." />
          <CommandList>
            <CommandEmpty>Aucun projet trouvé.</CommandEmpty>
            <CommandGroup>
              {activeProjects.map((project) => (
                <CommandItem
                  key={project.id}
                  value={`${project.name} ${project.client_name || ''}`}
                  onSelect={() => {
                    onValueChange(project.id);
                    setOpen(false);
                  }}
                  data-testid={`project-option-${project.id}`}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === project.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{project.name}</span>
                    {project.client_name && (
                      <span className="text-xs text-muted-foreground">
                        {project.client_name}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
